/**
 * `search` — qidiruv radari. docs/SPEC.md §search, faza 1.
 *
 * **Progress SOXTA BO'LMASIN** (SPEC). Bosqichlar prototipdagi taymerdan
 * emas, REAL matchmaking holatidan keladi:
 *
 *     match.queued   →  navbatda
 *     match.found    →  topildi
 *     match.timeout  →  hech kim yo'q, diamond QAYTARILDI
 *
 * Prototipda "topilmadi" holati umuman yo'q edi — u shu yerda qo'shilgan,
 * chunki qidiruv pullik (D-016) va foydalanuvchi pulining qayerga
 * ketganini bilishi kerak.
 */

import type { WsEvent } from '@voice/api-client';
import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useBackButton } from '../app/useBackButton';
import { uz } from '../i18n/uz';
import { useCancelSearch } from '../lib/api/useMatch';
import { useMe } from '../lib/api/useMe';
import { useRealtimeEvents, useRealtimeStatus } from '../lib/realtime/context';
import { fmt } from '../lib/format';
import { Avatar, CTAButton, Toast } from '../ui';
import { useToast } from '../ui/useToast';
import './screens.css';

type Stage = 'queued' | 'found' | 'empty';

export function Search() {
  const navigate = useNavigate();
  const me = useMe();
  const cancel = useCancelSearch();
  const toast = useToast();

  const location = useLocation();
  const nav = location.state as { othersWaiting?: number; timeoutSeconds?: number } | null;

  const [stage, setStage] = useState<Stage>('queued');
  const [waiting, setWaiting] = useState(nav?.othersWaiting ?? 0);
  const [timeout, setTimeoutSec] = useState(nav?.timeoutSeconds ?? 0);
  const [elapsed, setElapsed] = useState(0);
  // Timeout'da qaytarilgan diamond — SPEC.md:193 buni ko'rsatishni
  // majburiy qiladi. Jim qaytarish "pulim ketdi" degan tuyg'u qoldiradi.
  const [refunded, setRefunded] = useState(0);

  const onEvent = useCallback(
    (event: WsEvent) => {
      if (event.type === 'match.queued') {
        setStage('queued');
        setWaiting(event.others_waiting);
        setTimeoutSec(event.timeout_seconds);
      } else if (event.type === 'match.found') {
        // Navigatsiya BU YERDA EMAS — `App.tsx` dagi
        // `MatchFoundRedirect` da. Sabab: juftlikni YOPGAN odam bu
        // eventni `/search` ochilishidan OLDIN oladi va bu ekrandagi
        // tinglovchi uni umuman ko'rmasdi.
        //
        // Bu yerda faqat bosqich — ekran unmount bo'lgunicha ko'rinadi.
        setStage('found');
      } else if (event.type === 'match.timeout') {
        setStage('empty');
        setRefunded(event.refunded);
      }
    },
    // `navigate` endi bu yerda ishlatilmaydi — `match.found` ni
    // `App.tsx` hal qiladi.
    [],
  );

  useRealtimeEvents(onEvent);
  const status = useRealtimeStatus();

  // Progress REAL kutish vaqtidan hisoblanadi (SPEC.md:181) — soxta
  // "yuklanmoqda" chizig'i emas. Foydalanuvchi qachon tugashini ko'radi.
  useEffect(() => {
    const timer = setInterval(() => setElapsed((value) => value + 1), 1_000);
    return () => clearInterval(timer);
  }, []);

  const stop = useCallback(async () => {
    await cancel.mutateAsync().catch(() => undefined);
    navigate('/match');
  }, [cancel, navigate]);

  useBackButton(true, () => void stop());

  // Jim uzilib qolish TAQIQLANADI (docs/API.md §12) — aloqa uzilsa
  // foydalanuvchi buni bilishi kerak, aks holda u radarga qarab kutadi
  // va hech narsa kelmaydi.
  if (status === 'error') {
    return (
      <div className="screen">
        <div className="empty">{uz.search.disconnected}</div>
        <CTAButton variant="ghost" onClick={() => void stop()}>
          {uz.search.cancel}
        </CTAButton>
      </div>
    );
  }

  if (stage === 'empty') {
    return (
      <div className="screen">
        {toast.message && <Toast message={toast.message} />}
        <div className="empty">{uz.search.empty}</div>
        {refunded > 0 && <div className="search__refund">{uz.search.refunded(fmt(refunded))}</div>}
        <CTAButton variant="match" onClick={() => navigate('/match')}>
          {uz.match.start}
        </CTAButton>
      </div>
    );
  }

  const letter = me.data?.first_name.slice(0, 1) ?? '·';

  return (
    <div className="screen">
      {toast.message && <Toast message={toast.message} />}

      <div className="radar">
        <div className="radar__edge" />
        <div className="radar__ring" />
        <div className="radar__ring radar__ring--delayed" />
        <div className="radar__face">
          <Avatar letter={letter} paletteIdx={me.data?.palette_idx ?? 0} size={106} />
        </div>
      </div>

      <div className="search__stage">{stage === 'found' ? uz.search.found : uz.search.queued}</div>
      <div className="search__detail">
        {stage === 'found' ? uz.search.foundHint : uz.search.queuedHint(fmt(waiting))}
      </div>

      <div className="search__bar">
        <div
          className="search__bar-fill"
          style={{ width: `${timeout > 0 ? Math.min(100, (elapsed / timeout) * 100) : 0}%` }}
        />
      </div>

      <div style={{ marginTop: 32 }}>
        <CTAButton variant="ghost" disabled={cancel.isPending} onClick={() => void stop()}>
          {uz.search.cancel}
        </CTAButton>
      </div>
    </div>
  );
}
